import React from 'react';
import { AlertCircle } from 'lucide-react';

export interface FloatingLabelInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  error?: string;
  readOnly?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  type?: string;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
  className?: string;
  max?: string;
}

const FloatingLabelInput: React.FC<FloatingLabelInputProps> = ({
  label,
  value,
  onChange,
  required,
  error,
  readOnly,
  disabled,
  icon,
  type = 'text',
  multiline,
  rows,
  className = '',
  max,
}) => {
  const borderCls = error
    ? 'border-red-300 dark:border-red-500/50 focus-within:border-red-400 focus-within:ring-2 focus-within:ring-red-500/[0.08]'
    : 'border-slate-200 dark:border-white/[0.12] focus-within:border-brand-400 dark:focus-within:border-brand-400/60 focus-within:ring-2 focus-within:ring-brand-500/[0.08] hover:border-slate-300 dark:hover:border-white/[0.20]';

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {/* ── Label — above the input ── */}
      <label
        className={[
          'text-[11.5px] font-medium leading-none select-none',
          error
            ? 'text-red-500'
            : 'text-slate-500 dark:text-slate-400',
          disabled ? 'opacity-50' : '',
        ].join(' ')}
      >
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </label>

      {/* ── Input wrapper ── */}
      <div
        className={[
          'relative flex items-center rounded-lg border bg-white dark:bg-surface-dark-300 transition-all duration-150',
          borderCls,
          disabled ? 'opacity-50 pointer-events-none' : '',
        ].join(' ')}
      >
        {/* Left icon */}
        {icon && (
          <div className="pointer-events-none absolute left-3 text-slate-400 dark:text-slate-500 group-focus-within:text-brand-500 [&>svg]:h-[14px] [&>svg]:w-[14px]">
            {icon}
          </div>
        )}

        {multiline ? (
          <textarea
            rows={rows || 3}
            readOnly={readOnly}
            disabled={disabled}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder=""
            className={[
              'w-full bg-transparent text-[13.5px] font-normal text-slate-800 dark:text-slate-100 outline-none resize-none py-2.5',
              icon ? 'pl-9 pr-3' : 'pl-3 pr-3',
            ].join(' ')}
          />
        ) : (
          <input
            type={type}
            readOnly={readOnly}
            disabled={disabled}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder=""
            max={max}
            className={[
              'w-full bg-transparent text-[13.5px] font-normal text-slate-800 dark:text-slate-100 outline-none h-[40px]',
              icon ? 'pl-9 pr-3' : 'pl-3 pr-3',
            ].join(' ')}
          />
        )}
      </div>

      {error && (
        <div className="flex items-center gap-1 mt-0.5">
          <AlertCircle size={11} className="text-red-500 shrink-0" />
          <p className="text-[11px] text-red-500">{error}</p>
        </div>
      )}
    </div>
  );
};

export default FloatingLabelInput;
